import React, { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import analytics from '@/lib/analytics'
import { MIXPANEL_PROPERTIES } from '@/constants/mixpanel'

interface UserProperties {
  email?: string
  firstName?: string
  lastName?: string
  username?: string
}

interface AnalyticsContextType {
  identifyUser: (userId: string, properties?: UserProperties) => void
  setUserProperties: (properties: UserProperties) => void
  optIn: () => void
  optOut: () => void
  isTrackingEnabled: boolean
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
)

interface AnalyticsProviderProps {
  children: ReactNode
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false)

  // Use the analytics service's enabled state
  const isAnalyticsEnabled = analytics.isEnabled

  const identifyUser = (userId: string, properties?: UserProperties) => {
    if (!isAnalyticsEnabled) return

    const mixpanelProperties: Record<string, any> = {}

    if (properties) {
      if (properties.email) {
        mixpanelProperties[MIXPANEL_PROPERTIES.USER_EMAIL] = properties.email
      }
      if (properties.firstName) {
        mixpanelProperties[MIXPANEL_PROPERTIES.USER_FIRST_NAME] =
          properties.firstName
      }
      if (properties.lastName) {
        mixpanelProperties[MIXPANEL_PROPERTIES.USER_LAST_NAME] =
          properties.lastName
      }
    }

    analytics.identify(userId, mixpanelProperties)
    setIsTrackingEnabled(true)
  }

  const setUserProperties = (properties: UserProperties) => {
    if (!isAnalyticsEnabled) return

    const mixpanelProperties: Record<string, any> = {}

    if (properties.email) {
      mixpanelProperties[MIXPANEL_PROPERTIES.USER_EMAIL] = properties.email
    }
    if (properties.firstName) {
      mixpanelProperties[MIXPANEL_PROPERTIES.USER_FIRST_NAME] =
        properties.firstName
    }
    if (properties.lastName) {
      mixpanelProperties[MIXPANEL_PROPERTIES.USER_LAST_NAME] =
        properties.lastName
    }

    analytics.setUserProperties(mixpanelProperties)
  }

  const optIn = () => {
    if (!isAnalyticsEnabled) return
    analytics.optIn()
    setIsTrackingEnabled(true)
  }

  const optOut = () => {
    if (!isAnalyticsEnabled) return
    analytics.optOut()
    setIsTrackingEnabled(false)
  }

  const value: AnalyticsContextType = {
    identifyUser,
    setUserProperties,
    optIn,
    optOut,
    isTrackingEnabled: isAnalyticsEnabled && isTrackingEnabled,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}
